import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, FormControlLabel, FormControl, Radio, RadioGroup, MenuItem, Select, Tooltip } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./work_hold_list.scoped.scss";

interface State {
  gridApi?: GridApi
}

const defState: State = {
  gridApi: undefined
};

interface SearchParams {
  inoutType: string,
  holdDateFrom: string,
  holdDateTo: string,
  wh: string,
  woLike: string,
  hlLike: string,
  createDateFrom: string,
  createDateTo: string,
  trackingIdLike: string,
  visitNoLike: string,
  requesterLike: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  inoutType: "",
  holdDateFrom: dayjs().subtract(1, "month").format("YYYYMMDD"),
  holdDateTo: dayjs().format("YYYYMMDD"),
  wh: "",
  woLike: "",
  hlLike: "",
  createDateFrom: "",
  createDateTo: "",
  trackingIdLike: "",
  visitNoLike: "",
  requesterLike: "",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

const validationMsg = [
  {id: "reqInfos", type: "NotBlank", msg: "작업을 선택해 주세요."},
  {id: "workReqErpNo", type: "InvalidStatus", msg: "작업대기 상태만 보류해제할 수 있습니다."}
];

function WorkHoldList(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    // { headerName: "", field: "", width: 50,
    //   cellStyle: (p: any) => {
    //     if (p.data.inoutType == "I") {
    //       return { backgroundColor: "green" };
    //     } else {
    //       return { backgroundColor: "orange" };
    //     }
    //   }
    // },
    { headerName: "Hold Date", field: "holdDate", width: 180, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
    { headerName: "작업구분", field: "inoutType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutType") },
    { headerName: "상세구분", field: "inoutDetailType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutDetailType") },
    { headerName: "Creat Date", field: "createDate", width: 180, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
    { headerName: "창고", field: "wh", width: 80, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "Tracking ID", field: "trackingId", width: 120, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Work Shop", headerClass: "hd-ta-left", field: "workShop", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "W/O", headerClass: "hd-ta-left", field: "wo", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Visit No", headerClass: "hd-ta-left", field: "visitNo", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "기번(HL)", field: "hl", width: 120, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "요청자", field: "requester", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Request Qty", headerClass: "hd-ta-right", field: "reqQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Remain Qty", headerClass: "hd-ta-right", field: "remainQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Complete Qty", field: "completeQty", width: 130, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "multiRow" as const,
    },
    selectionColumnDef: {
      resizable: true
    }
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  // 바코드 스캔 기능 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
  });

  useEffect(() => {
    if (!isActiveTab) {
      return;
    }

    const handler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(handler);
    return () => kutil.stopBarcodeScan(handler);
  }, [isActiveTab]);
  // 끝: 바코드 스캔 기능 적용

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/inoutbound/work_hold_list.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/inoutbound/work_hold_list", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/inoutbound/work_hold_list", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/inoutbound/work_hold_list", {});
  }

  async function clearHold() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("보류해제 작업을 선택해 주세요.");
      return;
    }

    if (await kutil.confirm("보류해제하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/work_hold_list.proc?mode=clearHold", selectedInfos, {
        validationMsg: validationMsg
      });
      await kutil.alert("보류해제하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/inoutbound/work_hold_list.download?mode=search", searchParams, {
        "fileName": "작업보류목록_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  function selectTrackingId(trackingId: string) {
    const api = state.gridApi;
    if (!api) return;

    let found = false;
    api.forEachNode((node) => {
      if (node.data?.trackingId === trackingId) {
        node.setSelected(true);
        api.ensureNodeVisible(node, "middle");
        found = true;
      }
    });

    if (!found) {
      kutil.alert("일치하는 Tracking ID가 없습니다.\r\n" + trackingId);
    }
  }

  async function onBarcodeScan(code: string) {
    selectTrackingId(code);
  }

  return (
    <>
      {/* contents */}
      <div className="contents">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>작업 보류 목록</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
            { auth.write &&
            <Button variant="contained" onClick={clearHold}>보류해제</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="work-srch-area">
          <div>
            <p>작업구분</p>
            <RadioGroup
              value={searchParams.inoutType}
              onChange={(e) => {doSearch(e, {inoutType: e.target.value})}}
            >
              <FormControlLabel value="" control={<Radio size="small" />} label="전체" />
              {(commonCode.inoutType as any[]).map((code) => (
                <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
              ))}
            </RadioGroup>
          </div>
          <div className="divider ver"></div>
          <div className="srch_item">
            <div>
              <p>Hold Date</p>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD"
                  value={dayjs(searchParams.holdDateFrom)}
                  onChange={(e) => {doSearch(e, {holdDateFrom: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  maxDate={dayjs(searchParams.holdDateTo)}
                />
                -
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD"
                  value={dayjs(searchParams.holdDateTo)}
                  onChange={(e) => {doSearch(e, {holdDateTo: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  minDate={dayjs(searchParams.holdDateFrom)}
                />
              </LocalizationProvider>
            </div>
            <div>
              <p>창고</p>
              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <Select
                  value={searchParams.wh}
                  onChange={(e) => {doSearch(e, {wh: e.target.value});}}
                  displayEmpty
                >
                  <MenuItem value="">전체</MenuItem>
                  {(commonCode.wh as any[]).filter(c => c.code !== "B").map((code) => (
                    <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div>
              <p>Tracking ID</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.trackingIdLike}
                onChange={(e) => {updateSearchParams({ trackingIdLike: e.target.value })}}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="Tracking ID"
              />
            </div>
            <div>
              <p>W/O</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.woLike}
                onChange={(e) => {updateSearchParams({ woLike: e.target.value })}}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="W/O"
              />
            </div>
            <div>
              <p>Creat Date</p>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
                  format="YYYY-MM-DD"
                  value={dayjs(searchParams.createDateFrom)}
                  onChange={(e) => {doSearch(e, {createDateFrom: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  maxDate={dayjs(searchParams.createDateTo)}
                />
                -
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
                  format="YYYY-MM-DD"
                  value={dayjs(searchParams.createDateTo)}
                  onChange={(e) => {doSearch(e, {createDateTo: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  minDate={dayjs(searchParams.createDateFrom)}
                />
              </LocalizationProvider>
            </div>
            <div>
              <p>기번(HL)</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.hlLike}
                onChange={(e) => {updateSearchParams({ hlLike: e.target.value })}}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="기번(HL)"
              />
            </div>
            <div>
              <p> Visit No</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.visitNoLike}
                onChange={(e) => {updateSearchParams({ visitNoLike: e.target.value })}}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="Visit No"
              />
            </div>
            <div>
              <p>요청자</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.requesterLike}
                onChange={(e) => {updateSearchParams({ requesterLike: e.target.value })}}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="요청자"
              />
            </div>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 그리드 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              rowSelection={gridOptions.rowSelection}
              selectionColumnDef={gridOptions.selectionColumnDef}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
              totalPage={result.pageInfo.totalPage}
              currPage={result.pageInfo.page}
              onPageChange={goPage}
            />
          </div>
        </AgGridProvider>
        {/*// 그리드 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(WorkHoldList);
